import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';

const EditableCell = ({ 
  value, 
  onSave, 
  type = 'text', 
  options = [], 
  placeholder = '', 
  multiline = false,
  isEditable = true,
  className = '',
  displayValue = null,
  itemId = null,
  itemType = null,
  fieldName = null
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const { user, profile } = useAuth();


  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'email') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue);
      
      // Track edit history if we have the required props
      if (itemId && itemType && fieldName && user && profile) {
        try {
          await addDoc(collection(db, 'edit_history'), {
            item_id: itemId,
            item_type: itemType,
            field: fieldName,
            old_value: value || '',
            new_value: editValue || '',
            editor_id: user.uid,
            editor_name: profile.full_name || user.email,
            editor_role: profile.is_admin ? 'admin' : profile.is_writer ? 'writer' : profile.is_qa ? 'qa' : 'user',
            created_at: serverTimestamp()
          });
        } catch (historyError) {
          console.warn('Failed to save edit history:', historyError);
        }
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      setEditValue(value || ''); // Reset on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleClick = (e) => {
    if (isEditable && !isEditing) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  if (!isEditable) {
    return (
      <div className={`text-white ${className}`}>
        {displayValue || value || placeholder}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
        {type === 'select' ? (
          <select
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="bg-white/20 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary"
          >
            <option value="">Select...</option>
            {options.map(option => (
              <option key={option.value} value={option.value} className="bg-gray-800">
                {option.label}
              </option>
            ))}
          </select>
        ) : multiline ? (
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="bg-white/20 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary resize-none min-h-[60px] w-full"
            rows={3}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="bg-white/20 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-primary w-full"
          />
        )}
        
        {multiline && (
          <div className="flex flex-col space-y-1">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="p-1 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 disabled:opacity-50"
            >
              {isSaving ? (
                <Icon name="Loader2" size={12} className="animate-spin" />
              ) : (
                <Icon name="Check" size={12} />
              )}
            </button>
            <button
              onClick={handleCancel}
              className="p-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
            >
              <Icon name="X" size={12} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-colors group ${className}`}
    >
      <div className="flex items-center space-x-2">
        <span className="text-white">
          {displayValue || value || placeholder}
        </span>
        <Icon 
          name="Edit" 
          size={12} 
          className="text-white/40 group-hover:text-white/80 opacity-0 group-hover:opacity-100 transition-opacity" 
        />
      </div>
    </div>
  );
};

export default EditableCell;